import { useEffect, useState, useRef } from 'react'
import { Dialog, RadioGroup } from '@headlessui/react'
import { CultType } from 'web/lib/store'
import { useWeb3React } from '@web3-react/core'
import { BigNumber } from 'ethers'
import classNames from 'classnames'
import usePolygonBlockNumber from 'web/hooks/usePolygonBlockNumber'
import usePolygonForwarder from 'web/hooks/usePolygonForwarder'
import useBoundCult from 'web/hooks/useBoundCult'
import { useSWRConfig } from 'swr'
import { CacheKey } from 'web/lib/cache-keys'
import useCultLockupExpiry from 'web/hooks/useCultLockupExpiry'
import useRebaserData from 'web/hooks/useRebaserData'
import { Cult } from 'web/lib/gawd'
import usePolygonBlockTime from 'web/hooks/usePolygonBlockTime'

export default function CultStakingDialog({ isOpen, setIsOpen, onBinding }) {
  const { account } = useWeb3React()
  const { mutate } = useSWRConfig()
  const { polygonForwarderSubmit } = usePolygonForwarder()
  let focusButtonRef = useRef(null)

  // no need to load from contract, since contract only knows block #'s
  // will need to sync this manually
  const lockupPeriod = '1 week'

  // currently selected cult in the modal
  let [cultItem, setCultItem] = useState<string>(null)
  let [isBinding, setIsBinding] = useState(false)

  // handlers for actually cult bind and unbind
  const handleBind = () => {
    if (!cultItem) {
      console.error("No cult selected")
      return
    }

    setIsBinding(true)
    onBinding(true)

    polygonForwarderSubmit('CultBinder', 'bindOwner', [cultItem])
      .then((receipt) => {
        mutate(CacheKey.currentCult(account))
        mutate(CacheKey.cultLockupExpiry(account))
        mutate(CacheKey.cultPopulation())
      })
      .finally(() => {
        setIsBinding(false)
        onBinding(false)
        setIsOpen(false)
      })
  }

  const handleUnbind = () => {
    if (blocksRemaining.gt(0)) return

    setIsBinding(true)
    onBinding(true)

    polygonForwarderSubmit('CultBinder', 'unbindOwner', [])
      .then((receipt) => {
        mutate(CacheKey.currentCult(account))
        mutate(CacheKey.cultLockupExpiry(account))
        mutate(CacheKey.cultPopulation())
      })
      .finally(() => {
        setIsBinding(false)
        onBinding(false)
        setIsOpen(false)
      })
  }

  const currentCult = useBoundCult()
  const { cultStats } = useRebaserData()
  const lockupExpiry = useCultLockupExpiry()
  const polygonBlock = usePolygonBlockNumber()
  const polygonBlockTime = usePolygonBlockTime()
  const [blocksRemaining, setBlocksRemaining] = useState<BigNumber>(BigNumber.from(0))

  useEffect(() => {
    if (polygonBlock.gt(0) && lockupExpiry.gt(0)) {
      setBlocksRemaining(BigNumber.from(lockupExpiry).sub(polygonBlock))
    }
  }, [polygonBlock, currentCult, lockupExpiry])

  const astral = Cult.getById(CultType.Astral)
  const terrene = Cult.getById(CultType.Terrene)
  const arcane = Cult.getById(CultType.Arcane)

  // load state
  if (isBinding) {
    // return nothing while we are pending binding..
    return (<></>)
  }
  // unbind
  else if (currentCult != null) {
    return (
      <>
        <Dialog
          initialFocus={focusButtonRef}
          open={isOpen}
          onClose={setIsOpen}
          as="div"
          className="fixed inset-0 z-10 overflow-y-auto"
        >
          <div className="min-h-screen px-4 text-center">
            <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-90" />
            {/* This element is to trick the browser into centering the modal contents. */}
            <span className="inline-block h-screen align-middle" aria-hidden="true">
              &#8203;
            </span>


            <div className="space-y-6 inline-block w-full max-w-md p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-gray-800 shadow-xl rounded-lg">
              <h4 className="font-normal text-center">Your Gawds are Bound</h4>
              <p className="text-center text-gray-500">
                You have bound all your Gawds to the {currentCult.fullName}.
                {blocksRemaining.gt(0) && <>You are bound to this cult for {lockupPeriod}.</>}

                {/* expiry: {blocksRemaining} */}
                {/* remaining: {cultGame.lockupRemaining} */}
                {/* You can unbind and switch cults in {cultGame.lockupRemaining} Polygon blocks (approx {cultGame.lockupRemaining*2/60/60} hours) */}
              </p>
              <p className="text-center text-gray-500">
                Now visit each of your Gawds, resurrect them, and worship them regularly!
                &nbsp;<a href="https://gawds.notion.site/Blood-Token-dee95904528144bd8552c02506731f30" target="_blank" className="text-red-500">Learn more</a>
              </p>
              <button
                ref={focusButtonRef}
                className={classNames(`w-full`, blocksRemaining.gt(0) ? 'primaryButtonAlt' : 'primaryButton')}
                onClick={handleUnbind}
              >
                {blocksRemaining.gt(0) ?
                  <>
                    <span className="text-white">
                      Unbind in
                    </span>
                    <span className="font-bold">
                      &nbsp;{(blocksRemaining.mul(polygonBlockTime)).toNumber().timeForHumans()}
                    </span>
                  </>
                  :
                  <>Unbind from {currentCult.fullName}</>
                }
              </button>
            </div>
          </div>
        </Dialog>
      </>
    )
  }

  // bind
  return (
    <Dialog
      initialFocus={focusButtonRef}
      open={isOpen}
      onClose={setIsOpen}
      as="div"
      className="fixed inset-0 z-10 overflow-y-auto"
    >
      <div className="min-h-screen px-4 text-center">
        <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-90" />
        {/* This element is to trick the browser into centering the modal contents. */}
        <span className="inline-block h-screen align-middle" aria-hidden="true">
          &#8203;
        </span>

        <div className="space-y-6 inline-block w-full max-w-md p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-gray-800 shadow-xl rounded-lg">
          <div className="space-y-4 text-center">
            <Dialog.Title as="h4" className="font-normal font-serif text-3xl">
              Choose your Cult
            </Dialog.Title>

            <Dialog.Description className="text-gray-400">
              In order to earn $BLOOD from your Gawds, you must first bind them to a cult. An imbalance in the cults
              affects reward rates - choose wisely.
            </Dialog.Description>

            <Dialog.Description>Current lockup period: {lockupPeriod}</Dialog.Description>
          </div>

          <RadioGroup value={cultItem} onChange={setCultItem} className="space-y-4">
            <RadioGroup.Option value="1" className="bg-black rounded-md">
              {({ checked, active }) => (
                <div
                  className={classNames(
                    { 'outline-1': currentCult?.id == CultType.Arcane },
                    'grid grid-cols-5 items-center gap-4 p-4 rounded-md cursor-pointer border-2',
                    checked || active ? 'bg-red-500 bg-opacity-20 border-red-500' : 'bg-black border-transparent'
                  )}
                >
                  <div>
                    <img src="/images/arcane-icon.svg" alt="Arcane" />
                  </div>

                  <div className="self-start row-start-1 col-start-2 col-end-5">
                    <h5 className="text-base font-normal">Arcane Sect</h5>
                    <div className="text-gray-500">{cultStats[arcane.id].populationTotal} worshippers</div>
                    <div className="text-gray-500">{cultStats[arcane.id].gawdTotal} gawds</div>
                  </div>

                  <div className="self-start text-right row-start-1 col-start-4 col-end-6">
                    <div>+{cultStats[arcane.id].rewardBoost * 100}%</div>
                    <div className="text-gray-500">Reward boost</div>
                  </div>
                </div>
              )}
            </RadioGroup.Option>

            <RadioGroup.Option value="2" className="bg-black rounded-md">
              {({ checked, active }) => (
                <div
                  className={classNames(
                    { 'outline-1': currentCult?.id === CultType.Astral },
                    'grid grid-cols-5 items-center gap-4 p-4 rounded-md cursor-pointer border-2',

                    checked || active ? 'bg-red-500 bg-opacity-20 border-red-500' : 'bg-black border-transparent'
                  )}
                >
                  <div>
                    <img src="/images/astral-icon.svg" alt="Astral" />
                  </div>

                  <div className="row-start-1 col-start-2 col-end-5">
                    <h5 className="text-base font-normal">Astral Cult</h5>
                    <div className="text-gray-500">{cultStats[astral.id].populationTotal} worshippers</div>
                    <div className="text-gray-500">{cultStats[astral.id].gawdTotal} gawds</div>
                  </div>

                  <div className="self-start text-right row-start-1 col-start-4 col-end-6">
                    <div>+{cultStats[astral.id].rewardBoost * 100}%</div>
                    <div className="text-gray-500">Reward boost</div>
                  </div>
                </div>
              )}
            </RadioGroup.Option>

            <RadioGroup.Option value="3" className="bg-black rounded-md">
              {({ checked, active }) => (
                <div
                  className={classNames(
                    { 'outline-1': currentCult?.id === CultType.Terrene },
                    'grid grid-cols-5 items-center gap-4 p-4 rounded-md cursor-pointer border-2',

                    checked || active ? 'bg-red-500 bg-opacity-20 border-red-500' : 'bg-black border-transparent'
                  )}
                >
                  <div>
                    <img src="/images/terrene-icon.svg" alt="Terrene" />
                  </div>

                  <div className="row-start-1 col-start-2 col-end-5">
                    <h5 className="text-base font-normal">Terrene Division</h5>
                    <div className="text-gray-500">{cultStats[terrene.id].populationTotal} worshippers</div>
                    <div className="text-gray-500">{cultStats[terrene.id].gawdTotal} gawds</div>
                  </div>

                  <div className="self-start text-right row-start-1 col-start-4 col-end-6">
                    <div>+{cultStats[terrene.id].rewardBoost * 100}%</div>
                    <div className="text-gray-500">Reward boost</div>
                  </div>
                </div>
              )}
            </RadioGroup.Option>
          </RadioGroup>

          {cultItem && <button
            ref={focusButtonRef}
            className="primaryButton w-full"
            onClick={() => {
              handleBind()
            }}>
            Commence Binding
          </button>}
        </div>
      </div>
    </Dialog>
  )
}
